import { inject, Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChatMessage, ChatUser } from 'shared-types';

const PRESENCE_INTERVAL_MS = 5000; // heartbeat cadence
const POLLING_INTERVAL_MS = 2000; // poll for messages every 2s

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private readonly ngZone = inject(NgZone);
  private readonly http = inject(HttpClient);
  private readonly base = '/api/chat'; // via proxy to API

  private connected = false;
  private username: string | null = null;
  private presenceTimerId: number | null = null;
  private pollingTimerId: number | null = null;

  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private usersSubject = new BehaviorSubject<ChatUser[]>([]);
  readonly messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();
  readonly users$: Observable<ChatUser[]> = this.usersSubject.asObservable();

  connect(user: string) {
    if (this.connected && this.username === user) return;
    this.disconnect();
    this.username = user;
    this.connected = true;

    // Load initial messages and users
    this.loadMessages();
    this.loadUsers();

    // Start polling for new messages and presence updates
    this.ngZone.runOutsideAngular(() => {
      // Send presence heartbeat
      this.presenceTimerId = window.setInterval(() => {
        if (this.username) {
          this.updatePresence(this.username);
        }
      }, PRESENCE_INTERVAL_MS);

      // Poll for messages and users
      this.pollingTimerId = window.setInterval(() => {
        this.loadMessages();
        this.loadUsers();
      }, POLLING_INTERVAL_MS);
    });

    // Send initial presence
    this.updatePresence(user);
  }

  disconnect() {
    if (this.connected && this.username) {
      this.leaveChat(this.username);
    }
    if (this.presenceTimerId) {
      clearInterval(this.presenceTimerId);
      this.presenceTimerId = null;
    }
    if (this.pollingTimerId) {
      clearInterval(this.pollingTimerId);
      this.pollingTimerId = null;
    }
    this.connected = false;
    this.username = null;
  }

  send(text: string) {
    if (!this.connected || !this.username) return;
    this.http.post<ChatMessage>(`${this.base}/messages`, {
      user: this.username,
      text: text
    }).subscribe({
      next: () => {
        // Immediately reload messages to show the new one
        this.loadMessages();
      },
      error: (err) => console.error('Failed to send message:', err)
    });
  }

  private loadMessages() {
    this.http.get<ChatMessage[]>(`${this.base}/messages?limit=100`).subscribe({
      next: (messages) => {
        this.messagesSubject.next(messages);
      },
      error: (err) => console.error('Failed to load messages:', err)
    });
  }

  private loadUsers() {
    this.http.get<ChatUser[]>(`${this.base}/users`).subscribe({
      next: (users) => {
        this.usersSubject.next(users);
      },
      error: (err) => console.error('Failed to load users:', err)
    });
  }

  private updatePresence(user: string) {
    this.http.post(`${this.base}/presence`, { user }).subscribe({
      error: (err) => console.error('Failed to update presence:', err)
    });
  }

  private leaveChat(user: string) {
    this.http.post(`${this.base}/leave`, { user }).subscribe({
      error: (err) => console.error('Failed to leave chat:', err)
    });
  }
}
